import { TextField } from "@mui/material";
import { User } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { useSnackbar } from "notistack";
import React, { ChangeEvent, useState } from "react";
import { useAuthContext } from "../Auth";
import Button from "../components/UI/Button";
import { ProgressContainer } from "../components/UI/ProgressContainer";
import { fireFunctions } from "../firebase-config";
import { DBUser } from "../ostertalertypes";
import { handleError, InputState } from "../utils/utils";
import styles from "./Common.module.css";

interface IPageSetUser {
  user?: {user: User, userDetail: DBUser}
  onBackClick?: (changed: boolean) => void
}

const PageSetUser: React.FC<IPageSetUser>=(props) => {
  const defaultCountryCode = "+49";
  const pwHeplerText = "Hier nichts eintragen, dann bleibt Passwort gleich."
  const auth = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const authCtx = useAuthContext();

  const [email, setEmail] = useState<InputState>({ errorMessage: undefined, value: props.user?.user.email || "" });
  const [password, setPassword] = useState<InputState>({ errorMessage: undefined, value: "" });
  const [firstname, setFirstname] = useState<InputState>({ errorMessage: undefined, value: props.user?.userDetail.firstname || "" });
  const [lastname, setLastname] = useState<InputState>({ errorMessage: undefined, value: props.user?.userDetail.lastname || "" });
  const [phone, setPhone] = useState<InputState>({ errorMessage: undefined, value: props.user?.user.phoneNumber || defaultCountryCode });

  const isAccountInfoValid = (): string => {
    let msg: string = ""
    if (props.user?.user.email) {
      if ( email.value === "") {
        msg = msg + "E-Mail kann nur geändert, aber nicht gelöscht werden.\n"
      }
    } else { // create a new user with email and password
      // email is valide but password is empty
      if (email.errorMessage === undefined && email.value !== "" && password.value === ""){
        msg = msg + "Passwort und E-Mail muss angegeben werden oder keines von beidem\n"
      }
      // passwort is valid but email is empty)
      if ( password.errorMessage === undefined && password.value !== "" && email.value === "" ) {
        msg = msg + "Passwort und E-Mail muss angegeben werden oder keines von beidem\n"
      }
    }
    if ( password.errorMessage !== undefined) {
      msg = msg + password.errorMessage + "\n"
    }
    if ( email.errorMessage !== undefined) {
      msg = msg + email.errorMessage + "\n"
    }
    if ( firstname.errorMessage !== undefined || firstname.value === "") {
      msg = msg + "Vorname ist ein Pflichtfeld\n"
    }
    if ( lastname.errorMessage !== undefined || lastname.value === "") {
      msg = msg + "Nachname ist ein Pflichtfeld\n"
    }
    return msg;
  };

  async function onBtnClickHandler(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    const msg = isAccountInfoValid()
    if(msg.length > 0) {
      enqueueSnackbar(msg, {
        variant: "error",
        autoHideDuration: 6000,
        className: styles["success-snackbar"],
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      return;
    }

    try {
      setIsLoading(true);
      if (props.user) {
        const data = {
          userid: props.user.user.uid,
          email: email.value.length > 0 ? email.value : null,
          emailVerified: false,
          firstName: firstname.value,
          lastName: lastname.value,
          disabled: false,
          phone: phone.value !== defaultCountryCode ? phone.value : null,
          password: password.value.length > 0 ? password.value : null
        };
        await authCtx.updateUser(data);
        enqueueSnackbar("Benutzer " + firstname.value + " " + lastname.value + " erfolgreich aktualisiert", {
          variant: "success",
          persist: false,
          anchorOrigin: { vertical: "top", horizontal: "center" },
        });
        props.onBackClick && props.onBackClick(true);
      }
      else {
        await auth.registerAccount(email.value, password.value, firstname.value, lastname.value, phone.value === defaultCountryCode? undefined: phone.value);
        enqueueSnackbar("Benutzer " + firstname.value + " " + lastname.value + " erfolgreich angelegt", {
          variant: "success",
          persist: false,
          anchorOrigin: { vertical: "top", horizontal: "center" },
        });
        setEmail({ errorMessage: undefined, value: "" });
        setPassword({ errorMessage: undefined, value: "" });
        setFirstname({ errorMessage: undefined, value: "" });
        setLastname({ errorMessage: undefined, value: "" });
        setPhone({ errorMessage: undefined, value: defaultCountryCode });
      }
      setIsLoading(false);
    } catch (error) {
      handleError(props.user? "Fehler beim Aktualisieren des Benutzers" :"Fehler beim Anlegen des Benutzers", error, enqueueSnackbar, setIsLoading);
    }
  }

  function onEmailChange(event: ChangeEvent<HTMLInputElement>) {
    let errorMessage: string | undefined = "";
    const email: string = event.currentTarget.value.trim();
    // eslint-disable-next-line
    const regex = new RegExp(/.+\@.+\..+/)
    if (email.match(regex) == null && email.length > 0) {
      errorMessage = "E-Mailadresse ist nicht valide.";
    } else {
      errorMessage = undefined;
    }
    setEmail({ errorMessage: errorMessage, value: email });
  }

  function onPasswordChange(event: ChangeEvent<HTMLInputElement>) {
    let errorMessage: string | undefined = "";
    const password = event.currentTarget.value.trim();
    if (password.length > 0 && password.length < 6) {
      errorMessage = "Passwort muss mindestens 6 Zeichen sein.";
    } else {
      errorMessage = undefined;
    }
    setPassword({ errorMessage: errorMessage, value: event.currentTarget.value });
  }

  function onFirstnameChange(event: ChangeEvent<HTMLInputElement>) {
    let errorMessage: string | undefined = "";
    const firstname: string = event.currentTarget.value.trim();
    if (firstname.length === 0) {
      errorMessage = "Vorname darf nicht leer sein.";
    } else {
      errorMessage = undefined;
    }
    setFirstname({ errorMessage: errorMessage, value: firstname });
  }

  function onLastnameChange(event: ChangeEvent<HTMLInputElement>) {
    let errorMessage: string | undefined = "";
    const lastname: string = event.currentTarget.value.trim();
    if (lastname.length === 0) {
      errorMessage = "Nachname darf nicht leer sein.";
    } else {
      errorMessage = undefined;
    }
    setLastname({ errorMessage: errorMessage, value: lastname });
  }

  function onPhoneChange(event: ChangeEvent<HTMLInputElement>) {
    let errorMessage: string | undefined = "";
    const phone: string = event.currentTarget.value.trim();

    const regex = new RegExp(/^\+[1-9]\d{1,14}$/)
    if (phone.match(regex) == null) {
      errorMessage = "Nummer muss im Format +XXYYYYYY sein.";
    } else {
      errorMessage = undefined;
    }
    setPhone({ errorMessage: errorMessage, value: phone });
  }

  return (
    <ProgressContainer isLoading={isLoading} noBorder={props.user ? true : false}>
      <h1 className="text-2xl mt-0 mb-10 text-gray-500">{props.user? "Benutzer ändern": "Benutzer anlegen"}</h1>
      <p className="text-xs mb-5 text-gray-400">Pflichtfelder sind mit * markiert. Benutzer können auch ohne E-Mail und Passwort angelegt werden.
       Sie erhalten dann jedoch keine E-Mails und können keine Buchungsanfragen erstellen.</p>
      <form noValidate autoComplete="off">
        <TextField
          autoComplete="off"
          className="w-full mb-3"
          onChange={onEmailChange}
          helperText={email.errorMessage}
          error={email.errorMessage? true : false}
          id="inputEmail"
          value={email.value}
          label="E-Mailadresse"
          type="text"
          placeholder="beispiel@web.de"/>
        <TextField
          className="w-full mb-3"
          onChange={onPasswordChange}
          helperText={password.errorMessage}
          error={password.errorMessage? true : false}
          value={password.value}
          id="inputPassword"
          label="Passwort"
          placeholder={props.user ? pwHeplerText: undefined}
          type="text"/>
        <TextField
          className="w-full mb-3"
          onChange={onFirstnameChange}
          helperText={firstname.errorMessage}
          error={firstname.errorMessage? true : false}
          value={firstname.value}
          id="inputFirstName"
          label="Vorname*"
          type="text"/>
        <TextField
          className="w-full mb-3"
          onChange={onLastnameChange}
          helperText={lastname.errorMessage}
          error={lastname.errorMessage? true : false}
          value={lastname.value}
          id="inputLastName"
          label="Nachname*"
          type="text"/>
        <TextField
          className="w-full mb-3"
          onChange={onPhoneChange}
          helperText={phone.errorMessage}
          error={phone.errorMessage? true : false}
          value={phone.value}
          id="inputPhone"
          label="Telefon"
          type="text"/>
        <div className={`grid justify-items-end mt-10 gap-2 ${props.user? "grid-cols-2" : ""}`}>
          {props.user && <Button onClick={() => props.onBackClick && props.onBackClick(false)} className="w-full">Zurück</Button>}
          <Button onClick={onBtnClickHandler} disabled={isLoading} className="w-full">{props.user? "Ändern" : "Anlegen"}</Button>
        </div>
      </form>
    </ProgressContainer>
  );
}

export default PageSetUser;
